import { useState } from 'react';
import CustomToast from '@/components/CustomToast';
import SignupAccountForm from '@/modules/auth/components/SignupAccountForm';
import SignupUserForm from '@/modules/auth/components/SignupUserForm';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { SignupAccount, SignupUser } from '@/modules/auth/models/Signup';
import { SignupStepPath } from '@/modules/auth/paths';
import AuthService from '@/modules/auth/services/AuthService';
import { HomePath } from '@/routing/paths';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

interface State {
  user?: SignupUser;
  account?: SignupAccount;
}

const Signup = () => {
  const { login } = useAuth();
  const { step: stepParam } = useParams<{ step: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<State>({});

  const signup = async (user: SignupUser, account: SignupAccount) => {
    const authService = new AuthService();

    try {
      await authService.signUp(user, account);
    } catch (error) {
      console.error('Error while signup:', error);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={'Unable to complete the signup. Please try again later.'}
          variant={'error'}
          visible={t.visible}
        />
      ));
    }

    try {
      await login(user.username, user.password);

      navigate(HomePath);
    } catch (error) {
      console.error('Error while login after signup:', error);
    }
  };

  const handleSignupUserFormSubmit = async (data: SignupUser) => {
    setState((prevState) => ({
      ...prevState,
      user: { ...data },
    }));
    navigate(SignupStepPath.withStep('account'));
  };

  const handleSignupAccountSubmit = async (data: SignupAccount) => {
    setState((prevState) => ({
      ...prevState,
      account: { ...data },
    }));

    if (state.user) {
      await signup(state.user, data);
    }
  };

  const step = stepParam || 'user';

  return (
    <div className="relative flex min-h-screen items-center justify-start bg-gray-50 py-10 sm:justify-center sm:py-16">
      <div className="relative w-full px-4 sm:px-6">
        <div
          className="mb-6 flex items-center justify-center gap-2.5 sm:absolute sm:bottom-full sm:left-1/2 sm:-translate-x-1/2 sm:whitespace-nowrap"
          aria-label="SimplerSupply"
        >
          <img src="/logo.svg" alt="" className="h-10 w-10" />
          <span className="text-xl font-semibold tracking-tight text-gray-900">SimplerSupply</span>
        </div>

        {step === 'user' && (
          <SignupUserForm data={state.user} onSubmit={handleSignupUserFormSubmit} />
        )}
        {step === 'account' && <SignupAccountForm onSubmit={handleSignupAccountSubmit} />}
      </div>
    </div>
  );
};

export default Signup;
